import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react'

import { canUseWindow } from '@library/environment'

const DURATION_MS = 320

// useLayoutEffect warns on the server ("does nothing on the server") since this can
// render during SSR before its enclosing isEnabled guard resolves client-side.
const useIsomorphicLayoutEffect = canUseWindow() ? useLayoutEffect : useEffect

// Animates `contentRef`'s height between 0 and its natural height. CSS can't transition
// to/from `height: auto`, so this measures the content height and animates that instead,
// settling back to `auto` once open so later content changes still reflow correctly.
// `measure` defaults to scrollHeight; pass offsetHeight where scrollHeight under-reports.
export const useHeightCollapseTransition = (
  contentRef: RefObject<HTMLElement>,
  isOpen: boolean,
  measure: (el: HTMLElement) => number = (el) => el.scrollHeight,
) => {
  const [height, setHeight] = useState<string>(isOpen ? 'auto' : '0px')
  const isFirstRender = useRef(true)

  useIsomorphicLayoutEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false

      return undefined
    }

    const el = contentRef.current
    if (!el) return undefined

    if (isOpen) {
      setHeight(`${measure(el)}px`)
      const timeoutId = window.setTimeout(() => setHeight('auto'), DURATION_MS)

      return () => window.clearTimeout(timeoutId)
    }

    // Pin to the current pixel height first (transitioning from `auto` won't animate),
    // then drop to 0 next frame so the shrink actually animates. Both frame ids are
    // tracked so an interrupted close (reopened mid-animation) cancels whichever of the
    // two is still pending — otherwise a leftover second frame can fire after a reopen
    // and snap the height back to 0px.
    setHeight(`${measure(el)}px`)
    let innerFrameId: number | undefined
    const outerFrameId = window.requestAnimationFrame(() => {
      innerFrameId = window.requestAnimationFrame(() => setHeight('0px'))
    })

    return () => {
      window.cancelAnimationFrame(outerFrameId)
      if (innerFrameId !== undefined) window.cancelAnimationFrame(innerFrameId)
    }
  }, [contentRef, isOpen])

  return { height, transition: `height ${DURATION_MS}ms ease` }
}
